import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { listWorkspaces, createWorkspace, deleteWorkspace } from '../api/client'
import type { WorkspaceListItem } from '../types'

export default function WorkspaceList() {
  const [workspaces, setWorkspaces] = useState<WorkspaceListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [newWorkspaceName, setNewWorkspaceName] = useState('')
  const [creating, setCreating] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    loadWorkspaces()
  }, [])

  async function loadWorkspaces() {
    try {
      setLoading(true)
      setError(null)
      const result = await listWorkspaces()
      setWorkspaces(result.workspaces)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workspaces')
    } finally {
      setLoading(false)
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!newWorkspaceName.trim()) return

    try {
      setCreating(true)
      const result = await createWorkspace(newWorkspaceName.trim())
      navigate(`/workspace/${result.workspaceId}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create workspace')
    } finally {
      setCreating(false)
    }
  }

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Delete workspace "${name}"?`)) return

    try {
      await deleteWorkspace(id)
      setWorkspaces(workspaces.filter(w => w.workspaceId !== id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete workspace')
    }
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Your Workspaces</h1>

        <form onSubmit={handleCreate} className="flex gap-2">
          <input
            type="text"
            value={newWorkspaceName}
            onChange={e => setNewWorkspaceName(e.target.value)}
            placeholder="New workspace name..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <button
            type="submit"
            disabled={creating || !newWorkspaceName.trim()}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creating ? 'Creating...' : 'Create'}
          </button>
        </form>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {workspaces.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No workspaces</h3>
          <p className="mt-1 text-sm text-gray-500">
            Create your first C4 architecture workspace to get started.
          </p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg divide-y">
          {workspaces.map(workspace => (
            <div
              key={workspace.workspaceId}
              className="p-4 flex items-center justify-between hover:bg-gray-50"
            >
              <Link
                to={`/workspace/${workspace.workspaceId}`}
                className="flex-1 min-w-0"
              >
                <h3 className="text-lg font-medium text-gray-900 truncate">
                  {workspace.name}
                </h3>
                <p className="text-sm text-gray-500">
                  Updated {formatDate(workspace.updatedAt)}
                </p>
              </Link>

              <button
                onClick={() => handleDelete(workspace.workspaceId, workspace.name)}
                className="ml-4 p-2 text-gray-400 hover:text-red-500"
                title="Delete workspace"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
